import React, { useEffect, useMemo, useState } from "react";
import Icons from "../icons";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/* Browser's configured IANA zone — the sensible default (FR-SCH-02). */
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/25";
const labelCls = "mb-1.5 block text-sm font-medium text-slate-700";

/* Wizard steps, in order. */
const STEPS = [
  { id: "details", label: "Details", hint: "Name the schedule" },
  { id: "target", label: "Target", hint: "What to run" },
  { id: "when", label: "Schedule", hint: "When to run" },
  { id: "options", label: "Options", hint: "Env & notifications" },
  { id: "review", label: "Review", hint: "Confirm & save" },
];

/* Convert a stored ISO instant into the value a datetime-local input wants. */
function toLocalInput(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function fmtLocal(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/* Build the initial form model, prefilled from an existing schedule if editing. */
function initForm(schedule) {
  if (!schedule) {
    return {
      name: "",
      description: "",
      board_id: "",
      target_type: "testcases", // testcases | board
      target_testcasekeys: [],
      datasourcekey: "",
      schedule_type: "interval", // once | interval | cron
      run_at: "",
      interval_value: 1,
      interval_unit: "day",
      cron_expression: "0 2 * * *",
      timezone: LOCAL_TZ,
      environment: "",
      build: "latest",
      overlap_policy: "skip",
      notify_recipients: "",
      notify_on: "all",
      enabled: true,
    };
  }
  return {
    name: schedule.name || "",
    description: schedule.description || "",
    board_id: schedule.board_id || "",
    target_type: schedule.target_type || "testcases",
    target_testcasekeys: (schedule.target_testcasekeys || []).map(String),
    datasourcekey: schedule.datasourcekey || "",
    schedule_type: schedule.schedule_type || "interval",
    run_at: schedule.run_at ? toLocalInput(schedule.run_at) : "",
    interval_value: schedule.interval_value || 1,
    interval_unit: schedule.interval_unit || "day",
    cron_expression: schedule.cron_expression || "0 2 * * *",
    timezone: schedule.timezone || LOCAL_TZ,
    environment: schedule.environment || "",
    build: schedule.build || "latest",
    overlap_policy: schedule.overlap_policy || "skip",
    notify_recipients: (schedule.notify_recipients || []).join(", "),
    notify_on: schedule.notify_on || "all",
    enabled: schedule.enabled !== false,
  };
}

function cadenceText(form) {
  if (form.schedule_type === "once") {
    return form.run_at ? `Once at ${fmtLocal(new Date(form.run_at).toISOString())}` : "Once";
  }
  if (form.schedule_type === "interval") {
    const n = Number(form.interval_value) || 1;
    return `Every ${n} ${form.interval_unit}${n !== 1 ? "s" : ""}`;
  }
  return `Cron: ${form.cron_expression}`;
}

/* ------------------------------------------------------------------ */
/*  Stepper header                                                     */
/* ------------------------------------------------------------------ */
function Stepper({ current, onStepClick, maxReached }) {
  return (
    <ol className="flex items-center">
      {STEPS.map((step, idx) => {
        const isDone = idx < current;
        const isActive = idx === current;
        const clickable = idx <= maxReached;
        return (
          <li key={step.id} className="flex flex-1 items-center last:flex-none">
            <button
              type="button"
              disabled={!clickable}
              onClick={() => clickable && onStepClick(idx)}
              className={[
                "flex items-center gap-3 text-left",
                clickable ? "cursor-pointer" : "cursor-not-allowed",
              ].join(" ")}
            >
              <span
                className={[
                  "flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-semibold ring-1 transition",
                  isActive
                    ? "bg-brand-600 text-white ring-brand-600"
                    : isDone
                    ? "bg-brand-50 text-brand-700 ring-brand-600/30"
                    : "bg-white text-slate-400 ring-slate-300",
                ].join(" ")}
              >
                {isDone ? <Icons.Check className="h-5 w-5" /> : idx + 1}
              </span>
              <span className="hidden sm:block">
                <span
                  className={[
                    "block text-sm font-semibold",
                    isActive || isDone ? "text-slate-900" : "text-slate-400",
                  ].join(" ")}
                >
                  {step.label}
                </span>
                <span className="block text-xs text-slate-400">{step.hint}</span>
              </span>
            </button>
            {idx < STEPS.length - 1 && (
              <span
                className={[
                  "mx-3 h-px flex-1",
                  idx < current ? "bg-brand-500" : "bg-slate-200",
                ].join(" ")}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* ================================================================== */
/*  Wizard                                                             */
/* ================================================================== */
export default function ScheduleWizard({
  boards = [],
  userkey = null,
  schedule = null,
  onCancel,
  onSaved,
}) {
  const editing = Boolean(schedule);
  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);

  const [form, setForm] = useState(() => initForm(schedule));
  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const [timezones, setTimezones] = useState([LOCAL_TZ, "UTC"]);
  const [boardTestcases, setBoardTestcases] = useState([]);
  const [boardSubcats, setBoardSubcats] = useState([]);
  const [boardGroups, setBoardGroups] = useState([]);
  const [datasources, setDatasources] = useState([]);
  // Collapsed/expanded state for tree nodes (location + group ids).
  const [expanded, setExpanded] = useState({});

  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const goTo = (idx) => {
    setStep(idx);
    setMaxReached((m) => Math.max(m, idx));
  };

  // Timezone list for the picker (validated server-side).
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}schedules/meta/timezones`);
        const body = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(body?.data) && body.data.length) {
          setTimezones(body.data);
        }
      } catch {
        /* keep fallback */
      }
    })();
  }, []);

  // Load test cases + sub-categories + groups for the chosen board.
  useEffect(() => {
    if (!form.board_id) {
      setBoardTestcases([]);
      setBoardSubcats([]);
      setBoardGroups([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const bid = encodeURIComponent(form.board_id);
        const [tcRes, subRes, grpRes] = await Promise.all([
          fetch(`${API_BASE_URL}testcases?board_id=${bid}`),
          fetch(`${API_BASE_URL}boards/${bid}/subcategories`),
          fetch(`${API_BASE_URL}boards/${bid}/groups`),
        ]);
        const tcBody = await tcRes.json().catch(() => ({}));
        const subBody = await subRes.json().catch(() => ({}));
        const grpBody = await grpRes.json().catch(() => ({}));
        if (cancelled) return;
        setBoardTestcases(tcRes.ok && Array.isArray(tcBody?.data) ? tcBody.data : []);
        setBoardSubcats(subRes.ok && Array.isArray(subBody?.data) ? subBody.data : []);
        setBoardGroups(grpRes.ok && Array.isArray(grpBody?.data) ? grpBody.data : []);
      } catch {
        if (!cancelled) {
          setBoardTestcases([]);
          setBoardSubcats([]);
          setBoardGroups([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.board_id]);

  // Datasources are per-test-case, so offer them only when exactly one
  // explicit test case is selected (the dynamic-data case).
  const singleTestcaseKey =
    form.target_type === "testcases" && form.target_testcasekeys.length === 1
      ? form.target_testcasekeys[0]
      : null;

  useEffect(() => {
    if (!singleTestcaseKey) {
      setDatasources([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}datasources/by-testcase/${encodeURIComponent(singleTestcaseKey)}`
        );
        const body = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && Array.isArray(body?.data)) setDatasources(body.data);
      } catch {
        if (!cancelled) setDatasources([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [singleTestcaseKey]);

  const toggleTestcase = (key) =>
    setForm((f) => {
      const has = f.target_testcasekeys.includes(key);
      return {
        ...f,
        target_testcasekeys: has
          ? f.target_testcasekeys.filter((k) => k !== key)
          : [...f.target_testcasekeys, key],
      };
    });

  // Toggle a whole set of keys at once (a group, a sub-category, or board-level).
  // If everything is already selected we clear it, otherwise we add the lot.
  const toggleMany = (keys) => {
    setForm((f) => {
      const allSelected = keys.length > 0 && keys.every((k) => f.target_testcasekeys.includes(k));
      const set = new Set(f.target_testcasekeys);
      keys.forEach((k) => (allSelected ? set.delete(k) : set.add(k)));
      return { ...f, target_testcasekeys: Array.from(set) };
    });
  };

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !isOpen(id) }));
  // Locations open by default, groups closed by default.
  const isOpen = (id) => (id in expanded ? expanded[id] : id.startsWith("loc:"));

  // Build the navigable tree: Board level + each sub-category; within each,
  // its groups (test cases that run together) followed by any ungrouped cases.
  // Boards and sub-categories can both hold groups, so both levels are shown.
  const tree = useMemo(() => {
    const knownGroups = new Set(boardGroups.map((g) => String(g.groupkey)));
    const subKeyOf = (tc) => (tc.subcategorykey ? String(tc.subcategorykey) : null);
    const groupKeyOf = (tc) =>
      tc.groupkey && knownGroups.has(String(tc.groupkey)) ? String(tc.groupkey) : null;

    const locations = [
      { key: null, name: "Test Suite level", isBoard: true },
      ...boardSubcats.map((s) => ({ key: String(s.subcategorykey), name: s.name, isBoard: false })),
    ];

    return locations
      .map((loc) => {
        const locTests = boardTestcases.filter((tc) => subKeyOf(tc) === loc.key);
        const groupsHere = boardGroups.filter(
          (g) => (g.subcategorykey ? String(g.subcategorykey) : null) === loc.key
        );
        const groupNodes = groupsHere
          .map((g) => ({
            id: `grp:${g.groupkey}`,
            groupkey: String(g.groupkey),
            name: g.name,
            items: locTests.filter((tc) => groupKeyOf(tc) === String(g.groupkey)),
          }))
          .filter((n) => n.items.length > 0);
        const ungrouped = locTests.filter((tc) => groupKeyOf(tc) === null);
        const allKeys = locTests.map((tc) => String(tc.testcasekey));
        return {
          id: `loc:${loc.key ?? "board"}`,
          name: loc.name,
          isBoard: loc.isBoard,
          groupNodes,
          ungrouped,
          allKeys,
        };
      })
      .filter((loc) => loc.allKeys.length > 0);
  }, [boardTestcases, boardSubcats, boardGroups]);

  const selectionState = (keys) => {
    if (keys.length === 0) return { all: false, some: false };
    const sel = keys.filter((k) => form.target_testcasekeys.includes(k)).length;
    return { all: sel === keys.length, some: sel > 0 && sel < keys.length };
  };

  const boardName = boards.find((b) => b.id === form.board_id)?.name || form.board_id || "—";

  // Step gating.
  const canLeaveDetails = form.name.trim().length > 0;
  const canLeaveTarget =
    Boolean(form.board_id) &&
    (form.target_type === "board" || form.target_testcasekeys.length > 0);
  const canLeaveWhen =
    (form.schedule_type === "once" && Boolean(form.run_at)) ||
    (form.schedule_type === "interval" && Number(form.interval_value) >= 1 && Boolean(form.interval_unit)) ||
    (form.schedule_type === "cron" && form.cron_expression.trim().split(/\s+/).length === 5);

  const next = () => goTo(Math.min(step + 1, STEPS.length - 1));
  const back = () => goTo(Math.max(step - 1, 0));

  function buildPayload() {
    const p = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      board_id: form.board_id || null,
      target_type: form.target_type,
      timezone: form.timezone,
      environment: form.environment.trim() || null,
      build: form.build.trim() || null,
      overlap_policy: form.overlap_policy,
      notify_on: form.notify_on,
      notify_recipients: form.notify_recipients
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      enabled: form.enabled,
      schedule_type: form.schedule_type,
      userkey: userkey || null,
    };
    if (form.target_type === "testcases") {
      p.target_testcasekeys = form.target_testcasekeys;
      p.datasourcekey = singleTestcaseKey && form.datasourcekey ? form.datasourcekey : null;
    } else {
      p.target_testcasekeys = [];
    }
    if (form.schedule_type === "once") {
      p.run_at = form.run_at ? new Date(form.run_at).toISOString() : null;
    } else if (form.schedule_type === "interval") {
      p.interval_value = Number(form.interval_value) || 1;
      p.interval_unit = form.interval_unit;
    } else if (form.schedule_type === "cron") {
      p.cron_expression = form.cron_expression.trim();
    }
    return p;
  }

  const submit = async () => {
    setSaving(true);
    setSubmitError("");
    try {
      const payload = buildPayload();
      const url = editing
        ? `${API_BASE_URL}schedules/${schedule.schedulekey}`
        : `${API_BASE_URL}schedules`;
      const res = await fetch(url, {
        method: editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail =
          typeof body?.detail === "string"
            ? body.detail
            : JSON.stringify(body?.detail || `HTTP ${res.status}`);
        throw new Error(detail);
      }
      onSaved?.(editing ? "Schedule updated." : "Schedule created.");
    } catch (err) {
      setSubmitError(err.message || "Could not save the schedule.");
    } finally {
      setSaving(false);
    }
  };

  const targetSummary =
    form.target_type === "board"
      ? `Whole test suite · ${boardName}`
      : `${form.target_testcasekeys.length} test case${
          form.target_testcasekeys.length === 1 ? "" : "s"
        } · ${boardName}`;

  const recipientsList = form.notify_recipients
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          <Icons.ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            {editing ? "Edit schedule" : "New schedule"}
          </h1>
          <p className="text-sm text-slate-500">
            Run test cases and suites automatically — one-off, recurring or on a cron expression.
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-card p-5 shadow-sm">
        <Stepper current={step} onStepClick={goTo} maxReached={maxReached} />
      </div>

      {/* Step body */}
      <div className="rounded-xl border border-slate-200 bg-card p-6 shadow-sm">
        {/* ---- Step 1: Details ---- */}
        {step === 0 && (
          <div className="max-w-xl space-y-4">
            <div>
              <label className={labelCls}>Schedule name</label>
              <input
                autoFocus
                className={inputCls}
                value={form.name}
                onChange={(e) => setF({ name: e.target.value })}
                placeholder="e.g. Nightly smoke suite"
              />
            </div>
            <div>
              <label className={labelCls}>
                Description <span className="text-slate-400">(optional)</span>
              </label>
              <textarea
                rows={4}
                className={inputCls}
                value={form.description}
                onChange={(e) => setF({ description: e.target.value })}
                placeholder="What does this schedule run, and why?"
              />
            </div>
          </div>
        )}

        {/* ---- Step 2: Target ---- */}
        {step === 1 && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Test Suite</label>
                <select
                  className={inputCls}
                  value={form.board_id}
                  onChange={(e) =>
                    setF({ board_id: e.target.value, target_testcasekeys: [], datasourcekey: "" })
                  }
                >
                  <option value="">Select a test suite…</option>
                  {boards.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Scope</label>
                <select
                  className={inputCls}
                  value={form.target_type}
                  onChange={(e) => setF({ target_type: e.target.value })}
                >
                  <option value="testcases">Selected test cases</option>
                  <option value="board">All test cases in the test suite</option>
                </select>
              </div>
            </div>

            {form.target_type === "testcases" && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">
                    Test cases{" "}
                    {form.target_testcasekeys.length > 0 &&
                      `(${form.target_testcasekeys.length} selected)`}
                  </label>
                  <span className="text-xs text-slate-400">
                    Test suite › category › sub-category. Tick a sub-category or category to take everything under it.
                  </span>
                </div>
                {!form.board_id ? (
                  <p className="text-sm text-slate-400">Select a test suite first.</p>
                ) : boardTestcases.length === 0 ? (
                  <p className="text-sm text-slate-400">No test cases in this test suite.</p>
                ) : (
                  <div className="max-h-96 overflow-y-auto rounded-lg border border-slate-200">
                    {tree.map((loc) => {
                      const locSel = selectionState(loc.allKeys);
                      const locOpen = isOpen(loc.id);
                      return (
                        <div key={loc.id} className="border-b border-slate-100 last:border-b-0">
                          {/* Location row (board level or sub-category) */}
                          <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1.5">
                            <button
                              type="button"
                              onClick={() => toggleExpand(loc.id)}
                              className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                              aria-label={locOpen ? "Collapse" : "Expand"}
                            >
                              <Icons.ChevronRight
                                className={["h-4 w-4 transition-transform", locOpen ? "rotate-90" : ""].join(" ")}
                              />
                            </button>
                            <TriCheckbox
                              checked={locSel.all}
                              indeterminate={locSel.some}
                              onChange={() => toggleMany(loc.allKeys)}
                            />
                            {loc.isBoard ? (
                              <Icons.Boards className="h-4 w-4 flex-none text-slate-400" />
                            ) : (
                              <Icons.Grid className="h-4 w-4 flex-none text-slate-400" />
                            )}
                            <button
                              type="button"
                              onClick={() => toggleExpand(loc.id)}
                              className="flex-1 text-left text-xs font-semibold uppercase tracking-wider text-slate-600"
                            >
                              {loc.name}
                            </button>
                            <span className="text-xs text-slate-400">{loc.allKeys.length}</span>
                          </div>

                          {locOpen && (
                            <div className="py-1">
                              {/* Groups — selected as a whole (a group runs together) */}
                              {loc.groupNodes.map((g) => {
                                const keys = g.items.map((tc) => String(tc.testcasekey));
                                const gSel = selectionState(keys);
                                return (
                                  <label
                                    key={g.id}
                                    className={[
                                      "flex cursor-pointer items-center gap-2 py-1.5 pl-8 pr-2 text-sm transition",
                                      gSel.all ? "bg-brand-50/60" : "hover:bg-slate-50",
                                    ].join(" ")}
                                  >
                                    <TriCheckbox
                                      checked={gSel.all}
                                      indeterminate={gSel.some}
                                      onChange={() => toggleMany(keys)}
                                    />
                                    <span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700 ring-1 ring-brand-600/20">
                                      Sub-category
                                    </span>
                                    <span className="min-w-0 flex-1 truncate font-medium text-slate-700">
                                      {g.name}
                                    </span>
                                    <span className="flex-none text-xs text-slate-400">
                                      {keys.length} test{keys.length === 1 ? "" : "s"}
                                    </span>
                                  </label>
                                );
                              })}

                              {/* Ungrouped test cases in this location */}
                              {loc.ungrouped.map((tc) => (
                                <TestcaseRow
                                  key={String(tc.testcasekey)}
                                  tc={tc}
                                  depth={2}
                                  checked={form.target_testcasekeys.includes(String(tc.testcasekey))}
                                  onToggle={() => toggleTestcase(String(tc.testcasekey))}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {form.target_type === "board" && form.board_id && (
              <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                Every test case on <span className="font-medium">{boardName}</span> will run,
                including all categories.
              </p>
            )}

            {/* Datasource only meaningful for a single dynamic test case. */}
            {singleTestcaseKey && datasources.length > 0 && (
              <div className="max-w-md">
                <label className={labelCls}>Datasource (feeds dynamic step values)</label>
                <select
                  className={inputCls}
                  value={form.datasourcekey}
                  onChange={(e) => setF({ datasourcekey: e.target.value })}
                >
                  <option value="">Prompt for values (none)</option>
                  {datasources.map((d) => (
                    <option key={d.datasourcekey} value={d.datasourcekey}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* ---- Step 3: Schedule ---- */}
        {step === 2 && (
          <div className="max-w-xl space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Frequency</label>
                <select
                  className={inputCls}
                  value={form.schedule_type}
                  onChange={(e) => setF({ schedule_type: e.target.value })}
                >
                  <option value="once">Once (one-off)</option>
                  <option value="interval">Recurring (every N…)</option>
                  <option value="cron">Cron expression</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Time zone</label>
                <select
                  className={inputCls}
                  value={form.timezone}
                  onChange={(e) => setF({ timezone: e.target.value })}
                >
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {form.schedule_type === "once" && (
              <div>
                <label className={labelCls}>Run at</label>
                <input
                  type="datetime-local"
                  className={inputCls}
                  value={form.run_at}
                  onChange={(e) => setF({ run_at: e.target.value })}
                />
              </div>
            )}

            {form.schedule_type === "interval" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Every</label>
                  <input
                    type="number"
                    min="1"
                    className={inputCls}
                    value={form.interval_value}
                    onChange={(e) => setF({ interval_value: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>Unit</label>
                  <select
                    className={inputCls}
                    value={form.interval_unit}
                    onChange={(e) => setF({ interval_unit: e.target.value })}
                  >
                    <option value="minute">Minute(s)</option>
                    <option value="hour">Hour(s)</option>
                    <option value="day">Day(s)</option>
                    <option value="week">Week(s)</option>
                  </select>
                </div>
              </div>
            )}

            {form.schedule_type === "cron" && (
              <div>
                <label className={labelCls}>Cron expression (5 fields)</label>
                <input
                  className={`${inputCls} font-mono`}
                  value={form.cron_expression}
                  onChange={(e) => setF({ cron_expression: e.target.value })}
                  placeholder="0 2 * * 1-5"
                />
                <p className="mt-1 text-xs text-slate-400">
                  minute hour day-of-month month day-of-week — e.g. “0 2 * * 1-5” = 02:00 on
                  weekdays.
                </p>
              </div>
            )}

            <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
              Cadence: <span className="font-medium text-slate-800">{cadenceText(form)}</span>{" "}
              <span className="text-slate-400">({form.timezone})</span>
            </p>
          </div>
        )}

        {/* ---- Step 4: Options ---- */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className={labelCls}>Environment</label>
                <input
                  className={inputCls}
                  value={form.environment}
                  onChange={(e) => setF({ environment: e.target.value })}
                  placeholder="QA / VAL / …"
                />
              </div>
              <div>
                <label className={labelCls}>Build</label>
                <input
                  className={inputCls}
                  value={form.build}
                  onChange={(e) => setF({ build: e.target.value })}
                  placeholder="latest"
                />
              </div>
              <div>
                <label className={labelCls}>On overlap</label>
                <select
                  className={inputCls}
                  value={form.overlap_policy}
                  onChange={(e) => setF({ overlap_policy: e.target.value })}
                >
                  <option value="skip">Skip if still running</option>
                  <option value="queue">Queue (run after)</option>
                  <option value="allow">Allow concurrent</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <label className={labelCls}>Recipients (comma-separated emails)</label>
                <input
                  className={inputCls}
                  value={form.notify_recipients}
                  onChange={(e) => setF({ notify_recipients: e.target.value })}
                  placeholder="qa-lead@example.com, dev@example.com"
                />
              </div>
              <div>
                <label className={labelCls}>Notify on</label>
                <select
                  className={inputCls}
                  value={form.notify_on}
                  onChange={(e) => setF({ notify_on: e.target.value })}
                >
                  <option value="all">Every run</option>
                  <option value="failure">Failures only</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setF({ enabled: e.target.checked })}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
              />
              Enabled (uncheck to save without activating)
            </label>
          </div>
        )}

        {/* ---- Step 5: Review ---- */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <ReviewItem label="Name" value={form.name} />
              <ReviewItem label="Target" value={targetSummary} />
              <ReviewItem label="Cadence" value={`${cadenceText(form)} (${form.timezone})`} />
              <ReviewItem label="Environment" value={form.environment || "—"} />
              <ReviewItem label="Build" value={form.build || "—"} />
              <ReviewItem
                label="On overlap"
                value={
                  { skip: "Skip if running", queue: "Queue", allow: "Allow concurrent" }[
                    form.overlap_policy
                  ]
                }
              />
              <ReviewItem
                label="Notify"
                value={
                  recipientsList.length
                    ? `${recipientsList.join(", ")} · ${
                        form.notify_on === "all" ? "every run" : "failures only"
                      }`
                    : "No recipients"
                }
              />
              <ReviewItem label="Status" value={form.enabled ? "Enabled" : "Disabled"} />
              {form.description && (
                <div className="sm:col-span-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    Description
                  </p>
                  <p className="mt-1 text-sm text-slate-600">{form.description}</p>
                </div>
              )}
            </div>

            {submitError && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {submitError}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer nav */}
      <div className="mt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={step === 0 ? onCancel : back}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          {step === 0 ? (
            "Cancel"
          ) : (
            <>
              <Icons.ChevronLeft className="h-4 w-4" /> Back
            </>
          )}
        </button>

        {step < STEPS.length - 1 ? (
          <button
            type="button"
            onClick={next}
            disabled={
              (step === 0 && !canLeaveDetails) ||
              (step === 1 && !canLeaveTarget) ||
              (step === 2 && !canLeaveWhen)
            }
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue
            <Icons.ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <>
                <Icons.Spinner className="h-4 w-4 animate-spin" />
                {editing ? "Saving…" : "Creating…"}
              </>
            ) : (
              <>
                <Icons.Check className="h-4 w-4" />
                {editing ? "Save changes" : "Create schedule"}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function ReviewItem({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-medium text-slate-800">{value}</p>
    </div>
  );
}

/* Checkbox that also renders the tri-state (indeterminate) dash. */
function TriCheckbox({ checked, indeterminate, onChange }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = !checked && Boolean(indeterminate);
      }}
      onChange={onChange}
      className="h-4 w-4 flex-none rounded border-slate-300 text-brand-600 focus:ring-brand-600"
    />
  );
}

/* A single test-case leaf row, indented by tree depth. */
function TestcaseRow({ tc, depth, checked, onToggle }) {
  // depth 2 = ungrouped under a location, depth 3 = inside a group.
  const pad = depth >= 3 ? "pl-12" : "pl-8";
  return (
    <label
      className={[
        "flex cursor-pointer items-center gap-2 py-1.5 pr-2 text-sm transition",
        pad,
        checked ? "bg-brand-50/60 text-slate-800" : "text-slate-700 hover:bg-slate-50",
      ].join(" ")}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-4 w-4 flex-none rounded border-slate-300 text-brand-600 focus:ring-brand-600"
      />
      <span className="min-w-0 flex-1 truncate">{tc.name}</span>
      <span className="flex-none text-xs text-slate-400">{tc.priority}</span>
    </label>
  );
}
